import { useCallback, useEffect, useRef, useState } from 'react';
import { PlaybackController, type Slot, type PlayerMessageKey } from '../player/PlaybackController';
import { knownContentTypes } from '../player/renderers/registry';
import { applyStageConfig } from '../player/displayConfig';
import { contentRepo } from '../db/contentRepo';
import { prefs } from '../db/prefs';
import { runSync, type SyncProgress } from '../sync/downloader';
import { checkLogs, flushLogs } from '../sync/logSync';
import { cable, type CableMessageType } from '../socket/cable';
import { applyPendingUpdate, checkForUpdate, isUpdateReady, forceUpdate } from '../pwa/register';
import { shouldApplyUpdate } from '../pwa/updatePolicy';
import { useOnline } from '../helpers/useOnline';
import { useI18n } from '../i18n/I18nContext';
import { DeviceSettings } from '../components/DeviceSettings';
import type { StreamConfig } from '../types/stream';

const FALLBACK_INTERVAL_MS = 5 * 60 * 1000;

export function PlayerScreen() {
  const online = useOnline();
  const { t } = useI18n();
  const controllerRef = useRef<PlaybackController | null>(null);
  const stageRef = useRef<HTMLDivElement>(null);

  // Slot A element refs.
  const aRoot = useRef<HTMLDivElement>(null);
  const aVideo = useRef<HTMLVideoElement>(null);
  const aImg = useRef<HTMLImageElement>(null);
  const aFrame = useRef<HTMLIFrameElement>(null);
  const aQr = useRef<HTMLImageElement>(null);
  // Slot B element refs.
  const bRoot = useRef<HTMLDivElement>(null);
  const bVideo = useRef<HTMLVideoElement>(null);
  const bImg = useRef<HTMLImageElement>(null);
  const bFrame = useRef<HTMLIFrameElement>(null);
  const bQr = useRef<HTMLImageElement>(null);

  const [message, setMessage] = useState<PlayerMessageKey | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [progress, setProgress] = useState<SyncProgress>({ index: 0, total: 0 });
  const [config, setConfig] = useState<StreamConfig | null>(null);
  const [audioUnlocked, setAudioUnlocked] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const rotationRef = useRef(0);
  // Latest config, readable from callbacks that are created once (the boundary
  // handler and periodic loop) so they always see current update_preference.
  const configForUpdateRef = useRef<StreamConfig | null>(null);

  const applyConfig = useCallback((cfg: StreamConfig) => {
    setConfig(cfg);
    configForUpdateRef.current = cfg;
    controllerRef.current?.setConfig(cfg);
    if (stageRef.current) applyStageConfig(stageRef.current, cfg, rotationRef.current);
  }, []);

  const reloadPlaylist = useCallback(async (forceRestart: boolean) => {
    const rows = await contentRepo.getAll(knownContentTypes());
    const ctrl = controllerRef.current;
    if (!ctrl) return;
    await ctrl.load(rows, forceRestart);
    if (forceRestart) ctrl.start();
  }, []);

  const doSync = useCallback(async () => {
    if (!navigator.onLine) return;
    setSyncing(true);
    const result = await runSync((p) => setProgress(p));
    setSyncing(false);
    if (result.config) applyConfig(result.config);
    if (result.restart) await reloadPlaylist(true);
  }, [applyConfig, reloadPlaylist]);

  // One-time engine + lifecycle setup.
  useEffect(() => {
    const ctrl = new PlaybackController();
    controllerRef.current = ctrl;
    ctrl.onMessage = setMessage;

    // Apply a downloaded app update only when one is waiting AND the configured
    // update_preference window allows it. Called at content boundaries (and when
    // idle) so a new build never interrupts an item mid-playback.
    const tryApplyUpdate = () => {
      if (isUpdateReady() && shouldApplyUpdate(configForUpdateRef.current)) {
        void applyPendingUpdate();
      }
    };
    ctrl.onBoundary = tryApplyUpdate;

    if (
      aRoot.current &&
      aVideo.current &&
      aImg.current &&
      aFrame.current &&
      aQr.current &&
      bRoot.current &&
      bVideo.current &&
      bImg.current &&
      bFrame.current &&
      bQr.current
    ) {
      const slotA: Slot = {
        root: aRoot.current,
        video: aVideo.current,
        img: aImg.current,
        frame: aFrame.current,
        qr: aQr.current,
        objectUrl: null,
        content: null,
      };
      const slotB: Slot = {
        root: bRoot.current,
        video: bVideo.current,
        img: bImg.current,
        frame: bFrame.current,
        qr: bQr.current,
        objectUrl: null,
        content: null,
      };
      ctrl.setSlots(slotA, slotB);
    }

    let interval: ReturnType<typeof setInterval> | null = null;
    let cancelled = false;

    const boot = async () => {
      rotationRef.current = await prefs.getRotation();
      const cfg = await prefs.getConfig();
      if (cancelled) return;
      applyConfig(cfg);

      const rows = await contentRepo.getAll(knownContentTypes());
      if (cancelled) return;
      await ctrl.load(rows, true);
      if (cancelled) return;
      ctrl.start();

      // Refresh from the network shortly after boot.
      void doSync();

      // Live updates.
      const uuid = await prefs.getUuid();
      if (cancelled) return;
      if (uuid) cable.connect(uuid);

      // Periodic maintenance: sync, log flush, socket recovery, update check.
      // The browser rarely checks for a new SW on a long-running kiosk, so we
      // poll explicitly here (this only downloads; it never reloads). If an
      // update is already waiting and we're idle (no content boundary will
      // arrive to trigger it), apply it now.
      const periodMs = Math.max(30, cfg.refresh_rate) * 1000 || FALLBACK_INTERVAL_MS;
      interval = setInterval(() => {
        void doSync();
        void checkLogs();
        cable.check();
        void checkForUpdate();
        if (controllerRef.current?.isIdle()) tryApplyUpdate();
      }, periodMs);
    };

    void boot();

    const offCable = cable.onMessage((type: CableMessageType) => {
      if (type === 'stream.update') {
        void doSync();
      } else if (type === 'app.new_version') {
        // Download the new build now; it's applied at the next safe boundary
        // (or while idle) per update_preference, not mid-item.
        void checkForUpdate();
      } else if (type === 'app.force_update') {
        // Dashboard-triggered: apply the latest build immediately, ignoring the
        // update_preference window (fetches one first if none is waiting yet).
        void forceUpdate();
      }
    });

    return () => {
      cancelled = true;
      if (interval) clearInterval(interval);
      offCable();
      ctrl.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Hidden entry to the settings panel: 5 quick taps on the top-left corner, or
  // Ctrl+Shift+S on a keyboard. End users can't reach browser settings on a TV.
  const tapRef = useRef<{ count: number; t: number }>({ count: 0, t: 0 });
  const onSecretTap = useCallback(() => {
    const now = Date.now();
    const s = tapRef.current;
    if (now - s.t > 2000) s.count = 0;
    s.count += 1;
    s.t = now;
    if (s.count >= 5) {
      s.count = 0;
      setShowSettings(true);
    }
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && (e.key === 'S' || e.key === 's')) {
        setShowSettings(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Flush queued logs whenever connectivity returns.
  useEffect(() => {
    if (online) void flushLogs();
  }, [online]);

  // Auto-reboot at the configured local time (mirrors auto_reboot/reboot_time).
  useEffect(() => {
    if (!config?.auto_reboot || !config.reboot_time) return;
    const checkReboot = () => {
      const now = new Date();
      const hhmm = `${pad(now.getHours())}:${pad(now.getMinutes())}`;
      if (hhmm === config.reboot_time) {
        // If a new build is waiting, applying it reloads onto the new version;
        // otherwise just reboot the page as usual.
        if (isUpdateReady()) {
          void applyPendingUpdate();
        } else {
          window.location.reload();
        }
      }
    };
    const id = setInterval(checkReboot, 60_000);
    return () => clearInterval(id);
  }, [config?.auto_reboot, config?.reboot_time]);

  // Audio policy: content always autoplays (muted if the browser blocks sound).
  // When volume > 0, upgrade to sound on the first user interaction - without
  // ever blocking playback behind a gate. On true kiosk/signage builds, launch
  // the browser with an autoplay-allow flag and this never even matters.
  useEffect(() => {
    if (!config || config.volume <= 0 || audioUnlocked) return;
    const unlock = () => {
      setAudioUnlocked(true);
      controllerRef.current?.setAudioUnlocked(true);
    };
    window.addEventListener('pointerdown', unlock, { once: true });
    window.addEventListener('keydown', unlock, { once: true });
    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
  }, [config, audioUnlocked]);

  return (
    <div className="player-root">
      <div
        className="player-secret-hotspot"
        onPointerDown={onSecretTap}
        aria-hidden="true"
      />

      {!online && <div className="offline-badge">{t('common.offline')}</div>}

      <div className="player-stage" ref={stageRef}>
        <div className="player-layer" ref={aRoot}>
          <video ref={aVideo} muted playsInline preload="auto" />
          <img className="media" ref={aImg} alt="" />
          <iframe className="media frame" ref={aFrame} title="content-a" />
          <img className="player-qr" ref={aQr} alt="" />
        </div>
        <div className="player-layer" ref={bRoot}>
          <video ref={bVideo} muted playsInline preload="auto" />
          <img className="media" ref={bImg} alt="" />
          <iframe className="media frame" ref={bFrame} title="content-b" />
          <img className="player-qr" ref={bQr} alt="" />
        </div>
      </div>

      {message === 'no_content' && (
        <div className="player-message">{t('player.noContent')}</div>
      )}

      {syncing && progress.total > 0 && (
        <div className="player-sync">
          {progress.index + 1}/{progress.total}
        </div>
      )}

      {showSettings && <DeviceSettings onClose={() => setShowSettings(false)} />}
    </div>
  );
}

function pad(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}
